import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { useDebounceEffect } from 'ahooks';
import { Form } from 'antd';
import _, { snakeCase } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLatest } from 'hooks/use-latest';
import { useTranslation } from 'hooks/use-translation';
import { NumberField } from '../fields';
import {
  isOptional,
  NumberFieldFilter,
  NumberFilterOperationType,
  NumberFilterOperationTypes,
} from '../filters/number';
import { DurationInput, NumericInput, Select } from './components';
import { CyWrapper } from './wrappers/cy-wrapper';

interface Props {
  filter: NumberFieldFilter;
  onChange: (filter: NumberFieldFilter) => void;
  getFilterOperationTypeOptions?: (field: NumberField) => NumberFieldFilter[];
  getOperationOptionRenderer?: (
    type: NumberFilterOperationType,
  ) => null | JSX.Element;
}

const defaultOptions = (field: NumberField): NumberFieldFilter[] =>
  NumberFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as NumberFieldFilter),
  );

const durationInputFields = [
  'effort',
  'timeEntryMinutes',
  'totalMinutes',
  'trackedMinutes',
  'billableMinutes',
  'nonBillableMinutes',
  'trackedMins',
  'billableMins',
  'nonBillableMins',
  'allocatedMins',
  'softAllocatedMins',
  'hardAllocatedMins',
  'budgetedMinutes',
  'remainingMinutes',
  'totalAllocatedHours',
];

export const NumberCriteria: FC<Props> = ({
  filter,
  onChange,
  getFilterOperationTypeOptions = defaultOptions,
  getOperationOptionRenderer,
}) => {
  const effortInputPlaceholder = useTranslation(
    'timesheets.placeholders.time_tracked',
  );

  const [number, setNumber] = useState<number | undefined>(
    (filter as any).value,
  );
  const filterNumber = (filter as any).value as number | undefined;
  const latestOnChange = useLatest(onChange);

  useEffect(() => {
    setNumber(filterNumber);
  }, [filterNumber, setNumber]);
  const filterRef = useRef(filter);

  useEffect(() => {
    filterRef.current = filter;
  }, [filterRef, filter]);
  useDebounceEffect(
    () => {
      latestOnChange({
        ...filterRef.current,
        value: number,
      } as any);
    },
    [filterRef, number, latestOnChange],
    {
      wait: 300,
      trailing: true,
    },
  );

  const _onChange = useCallback(
    (selected) =>
      onChange({
        ...filter,
        type: selected,
      } as any),
    [filter, onChange],
  );

  return (
    <>
      <Form.Item>
        <Select
          disabled={filter == null}
          value={filter?.type}
          onChange={_onChange}
          suffixIcon={<ChevronDown16 />}
        >
          {(filter?.field == null
            ? []
            : getFilterOperationTypeOptions(filter.field)
          ).map((option) => (
            <Select.Option key={option.type} value={option.type}>
              <CyWrapper filter={filter} option={option}>
                {getOperationOptionRenderer?.(option.type) ?? (
                  <FormattedMessage
                    id={`task_list.filters.${snakeCase(
                      NumberFilterOperationType[option.type],
                    )}`}
                  />
                )}
              </CyWrapper>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {filter != null &&
        (filter.type === NumberFilterOperationType.Is ||
          filter.type === NumberFilterOperationType.IsGreaterThan ||
          filter.type === NumberFilterOperationType.IsLessThan) && (
          <Form.Item>
            {durationInputFields.includes(filter.field.fieldName) ? (
              <DurationInput
                data-cy={`select.fields.input.${
                  filter?.field?.fieldLabel ?? '__new__'
                }`}
                placeholder={effortInputPlaceholder}
                value={filter.value}
                min={filter.field.metaData?.range?.min}
                max={filter.field.metaData?.range?.max}
                onChange={(value) => {
                  if ((filter.value ?? -1) !== (value ?? -1)) {
                    setNumber(
                      value == null || value.toString().trim().length === 0
                        ? undefined
                        : parseInt(value.toString(), 10),
                    );
                  }
                }}
              />
            ) : (
              <NumericInput
                data-cy={`select.fields.input.${
                  filter?.field?.fieldLabel ?? '__new__'
                }`}
                value={_.isFinite(filter.value) ? filter.value : undefined}
                min={filter.field.metaData?.range?.min}
                max={filter.field.metaData?.range?.max}
                onChange={(value) => {
                  setNumber(
                    value == null || value.toString().trim().length === 0
                      ? undefined
                      : Number(value),
                  );
                }}
              />
            )}
          </Form.Item>
        )}
    </>
  );
};
